import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { 
  Mail, Send, Users, Tag, Clock, TrendingUp, AlertCircle, 
  ChevronRight, Play, Pause, Plus, Edit, Trash2, Eye,
  RefreshCw, Filter, CheckCircle, XCircle, Calendar
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AdminEmailMarketing() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [campaigns, setCampaigns] = useState([]);
  const [segments, setSegments] = useState([]);
  const [emailQueue, setEmailQueue] = useState({ emails: [], counts: {} });
  const [tagRules, setTagRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('campaigns');

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [campaignsRes, segmentsRes, queueRes, tagsRes] = await Promise.all([
        fetch(`${API_URL}/api/email/campaigns`),
        fetch(`${API_URL}/api/email/segments`),
        fetch(`${API_URL}/api/email/queue?limit=20`),
        fetch(`${API_URL}/api/email/tags/rules`)
      ]);
      
      if (campaignsRes.ok) setCampaigns(await campaignsRes.json());
      if (segmentsRes.ok) setSegments(await segmentsRes.json());
      if (queueRes.ok) setEmailQueue(await queueRes.json());
      if (tagsRes.ok) setTagRules(await tagsRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  const toggleCampaign = async (campaignId) => {
    try {
      const res = await fetch(`${API_URL}/api/email/campaigns/${campaignId}/toggle`, {
        method: 'PUT'
      });
      if (res.ok) {
        fetchData();
      }
    } catch (error) {
      console.error('Error toggling campaign:', error);
    }
  };

  const getTriggerLabel = (trigger) => {
    const labels = {
      'user_registered': 'Registro de usuario',
      'warranty_created': 'Garantía creada',
      'warranty_completed': 'Garantía completada',
      'warranty_expiring': 'Garantía por expirar',
      'user_inactive_60_days': 'Usuario inactivo 60 días'
    };
    return labels[trigger] || trigger;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-email-marketing">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Email Marketing</h1>
              <p className="text-gray-600 mt-1">Campañas automatizadas, segmentación y etiquetado</p>
            </div>
            <Button onClick={() => navigate('/admin')} variant="outline">
              Volver al Panel
            </Button>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Campañas Activas</p>
                  <p className="text-2xl font-bold">{campaigns.filter(c => c.is_active).length}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <Play className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Segmentos</p>
                  <p className="text-2xl font-bold">{segments.length}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Emails Pendientes</p>
                  <p className="text-2xl font-bold">{emailQueue.counts?.pending || 0}</p>
                </div>
                <div className="p-3 bg-yellow-100 rounded-full">
                  <Mail className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Emails Enviados</p>
                  <p className="text-2xl font-bold">{emailQueue.counts?.sent || 0}</p>
                </div>
                <div className="p-3 bg-purple-100 rounded-full">
                  <Send className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="campaigns">
              <Mail className="w-4 h-4 mr-2" />
              Campañas Drip
            </TabsTrigger>
            <TabsTrigger value="segments">
              <Users className="w-4 h-4 mr-2" />
              Segmentos
            </TabsTrigger>
            <TabsTrigger value="tags">
              <Tag className="w-4 h-4 mr-2" />
              Auto-etiquetado
            </TabsTrigger>
            <TabsTrigger value="queue">
              <Clock className="w-4 h-4 mr-2" />
              Cola de Emails
            </TabsTrigger>
          </TabsList>

          {/* Campaigns Tab */}
          <TabsContent value="campaigns">
            <div className="grid gap-4">
              {campaigns.map((campaign) => (
                <Card key={campaign.campaign_id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg font-semibold">{campaign.name}</h3>
                          <Badge variant={campaign.is_active ? "default" : "secondary"}>
                            {campaign.is_active ? 'Activa' : 'Pausada'}
                          </Badge>
                        </div>
                        <p className="text-gray-600 mt-1">{campaign.description}</p>
                        
                        <div className="flex items-center gap-4 mt-4">
                          <div className="flex items-center gap-1 text-sm text-gray-500">
                            <AlertCircle className="w-4 h-4" />
                            <span>Trigger: {getTriggerLabel(campaign.trigger)}</span>
                          </div>
                          <div className="flex items-center gap-1 text-sm text-gray-500">
                            <Mail className="w-4 h-4" />
                            <span>{campaign.emails?.length || 0} emails en secuencia</span>
                          </div>
                        </div>

                        {/* Email sequence preview */}
                        <div className="mt-4 flex items-center gap-2 overflow-x-auto pb-2">
                          {campaign.emails?.map((email, idx) => (
                            <React.Fragment key={idx}>
                              <div className="flex-shrink-0 px-3 py-2 bg-gray-100 rounded-lg text-sm">
                                <div className="font-medium">{email.subject || email.template}</div>
                                <div className="text-xs text-gray-500">
                                  {email.delay_hours > 0 ? `+${email.delay_hours}h` : 'Inmediato'}
                                </div>
                              </div>
                              {idx < campaign.emails.length - 1 && (
                                <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 ml-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleCampaign(campaign.campaign_id)}
                        >
                          {campaign.is_active ? (
                            <Pause className="w-4 h-4" />
                          ) : (
                            <Play className="w-4 h-4" />
                          )}
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Segments Tab */}
          <TabsContent value="segments">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {segments.map((segment) => (
                <Card key={segment.segment_id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg">{segment.name}</CardTitle>
                      <Badge variant={segment.is_active ? "default" : "secondary"}>
                        {segment.is_active ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </div>
                    <CardDescription>{segment.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="text-sm">
                        <span className="text-gray-500">Filtros:</span>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {Object.entries(segment.filters || {}).map(([key, value]) => (
                            <Badge key={key} variant="outline" className="text-xs">
                              {key}: {Array.isArray(value) ? value.join(', ') : value}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      {segment.member_count !== undefined && (
                        <div className="flex items-center gap-2 text-sm">
                          <Users className="w-4 h-4 text-gray-400" />
                          <span>{segment.member_count} miembros</span>
                        </div>
                      )}
                    </div>
                    <Button variant="outline" className="w-full mt-4" size="sm">
                      <Eye className="w-4 h-4 mr-2" />
                      Ver Miembros
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Auto-tags Tab */}
          <TabsContent value="tags">
            <Card>
              <CardHeader>
                <CardTitle>Reglas de Auto-etiquetado</CardTitle>
                <CardDescription>
                  Los usuarios se etiquetan automáticamente basado en su comportamiento y productos
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {tagRules.map((rule, idx) => (
                    <div 
                      key={rule.rule_id || idx}
                      className="p-4 border rounded-lg hover:border-blue-300 transition-colors"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Tag className="w-4 h-4 text-blue-600" />
                        <span className="font-medium">{rule.tag}</span>
                      </div>
                      <div className="text-sm text-gray-500">
                        {rule.conditions?.product_keywords && (
                          <p>Keywords: {rule.conditions.product_keywords.join(', ')}</p>
                        )}
                        {rule.conditions?.min_warranties && (
                          <p>Min. garantías: {rule.conditions.min_warranties}</p>
                        )}
                        {rule.conditions?.min_total_spent && (
                          <p>Min. gastado: {rule.conditions.min_total_spent}€</p>
                        )}
                        {rule.conditions?.min_rating && (
                          <p>Min. rating: {rule.conditions.min_rating}⭐</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Email Queue Tab */}
          <TabsContent value="queue">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Cola de Emails</CardTitle>
                    <CardDescription>Emails pendientes de envío</CardDescription>
                  </div>
                  <Button onClick={fetchData} variant="outline" size="sm">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Actualizar
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Queue stats */}
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="text-center p-3 bg-yellow-50 rounded-lg">
                    <div className="text-2xl font-bold text-yellow-600">{emailQueue.counts?.pending || 0}</div>
                    <div className="text-sm text-gray-500">Pendientes</div>
                  </div>
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{emailQueue.counts?.scheduled || 0}</div>
                    <div className="text-sm text-gray-500">Programados</div>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{emailQueue.counts?.sent || 0}</div>
                    <div className="text-sm text-gray-500">Enviados</div>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-lg">
                    <div className="text-2xl font-bold text-red-600">{emailQueue.counts?.failed || 0}</div>
                    <div className="text-sm text-gray-500">Fallidos</div>
                  </div>
                </div>

                {/* Email list */}
                <div className="space-y-2">
                  {emailQueue.emails?.map((email) => (
                    <div 
                      key={email.email_id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex-1">
                        <div className="font-medium">{email.subject}</div>
                        <div className="text-sm text-gray-500">
                          Para: {email.to_email} • {new Date(email.created_at).toLocaleString('es-ES')}
                        </div>
                      </div>
                      <Badge variant={
                        email.status === 'sent' ? 'default' :
                        email.status === 'pending' ? 'secondary' :
                        email.status === 'failed' ? 'destructive' : 'outline'
                      }>
                        {email.status === 'sent' && <CheckCircle className="w-3 h-3 mr-1" />}
                        {email.status === 'failed' && <XCircle className="w-3 h-3 mr-1" />}
                        {email.status}
                      </Badge>
                    </div>
                  ))}
                  
                  {emailQueue.emails?.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      No hay emails en cola
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default AdminEmailMarketing;
